import {v4} from 'uuid';

import {logger} from './logger';
import type {IframeProps, IframeInitPosition, PluginIframe} from './types';
import {
    DEFAULT_FLOATING_IFRAME_HEIGHT,
    DEFAULT_FLOATING_IFRAME_WIDTH,
} from './constants';

export function generateId(readableName?: string) {
    return `${readableName ? `${readableName}-` : ''}${v4()}`;
}

export function createPopupManager() {
    const popups = new Map<
        string,
        {
            popup: ReturnType<Window['open']>;
            shouldOpen?: (input: Record<string, unknown>) => boolean;
        }
    >();
    const intervals = new Set<number>();

    window.onbeforeunload = () => {
        intervals.forEach(value => clearInterval(value));
        intervals.clear();
    };

    const add = (
        id: string,
        shouldOpen?: (input: Record<string, unknown>) => boolean,
    ) => {
        if (popups.has(id)) {
            logger.warn(
                `A popup with with ID:'${id}' has already been registered`,
            );
            return;
        }
        popups.set(id, {popup: null, shouldOpen});
    };

    const remove = (id: string) => {
        popups.delete(id);
    };

    const open = (
        id: string,
        openParams: Parameters<Window['open']>,
        context?: Record<string, unknown>,
    ) => {
        if (!popups.has(id)) {
            popups.set(id, {popup: null});
        }
        const popupCtx = popups.get(id);
        if (popupCtx?.popup && !popupCtx.popup.closed) {
            logger.warn(`Popup with ID:'${id}' is currently opened`);
            return;
        }
        if (
            popups.get(id)?.shouldOpen &&
            !popups.get(id)?.shouldOpen?.(context ?? {})
        ) {
            return;
        }

        const popup = window.open(...openParams);
        if (popupCtx) {
            popupCtx.popup = popup;
        }
        if (popup) {
            const intervalId = window.setInterval(() => {
                if (popup.closed) {
                    const popupWrapper = popups.get(id);
                    if (popupWrapper) {
                        popupWrapper.popup = null;
                    }
                    clearInterval(intervalId);
                    intervals.delete(intervalId);
                }
            }, 500);
            intervals.add(intervalId);
        }
    };

    const get = (id: string) => {
        return popups.get(id)?.popup;
    };

    return {add, remove, open, get};
}

export function createIframeManager() {
    const iframes = new Map<string, PluginIframe>();

    const add = ({id, url, type, ...rest}: IframeProps) => {
        if (iframes.has(id)) {
            logger.warn(
                `An iframe with with ID:'${id}' has already been registered`,
            );
            return;
        }
        const {headerTitle, initPosition} = {...rest};
        const iframe = document.createElement('iframe');
        iframe.src = url;
        const position: IframeInitPosition = initPosition ?? {
            width: DEFAULT_FLOATING_IFRAME_WIDTH,
            height: DEFAULT_FLOATING_IFRAME_HEIGHT,
        };
        iframes.set(id, {
            element: iframe,
            type,
            headerTitle,
            initPosition: position,
        });
        return iframe;
    };

    const remove = (id: string) => {
        iframes.delete(id);
    };

    const get = (id: string) => {
        return iframes.get(id);
    };

    return {add, remove, open, get};
}
